// src/pages/Pacientes.js

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Home, AlertTriangle, Users, ChevronLeft, ChevronRight, Activity, MapPin, CalendarClock, Check, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FilterControls from '../components/FilterControls';
import { usePatientFilters } from '../hooks/usePatientFilters';
import { apiFetch } from '../utils/apiFetch';
import '../Acompanhamento.css';

// Inject font
const _style = document.createElement('style');
_style.textContent = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`;
document.head.appendChild(_style);

// --- HELPERS ---
const calculateAge = (dtNascimento) => {
    if (!dtNascimento) return 0;
    const birthDate = new Date(dtNascimento); const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const correctedDate = new Date(date.getTime() + userTimezoneOffset);
    if (isNaN(date.getTime())) return '-';
    return correctedDate.toLocaleDateString('pt-BR');
};

const getAddressKey = (p) => {
    const rua = (p.endereco || 'ZZ_SEM_ENDERECO').trim().toLowerCase();
    const num = (p.numero || 'S/N').trim().toLowerCase();
    const comp = (p.complemento || '').trim().toLowerCase();
    return `${rua}|${num}|${comp}`;
};

const formatAddressDisplay = (p) => {
    if (!p.endereco) return "Endereço Não Informado";
    const rua = p.endereco.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    const num = p.numero ? `, ${p.numero}` : '';
    const comp = p.complemento ? ` (${p.complemento})` : '';
    return `${rua}${num}${comp}`;
};

const ConditionBadge = ({ type, active, missingInfo }) => {
    if (!active) return null;
    const styles = {
        DM: { background: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' },
        HAS: { background: '#e0f2fe', color: '#075985', borderColor: '#bae6fd' },
        Gestante: { background: '#fce7f3', color: '#9d174d', borderColor: '#fbcfe8' },
        Idoso: { background: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' },
    };
    const labels = { DM: 'DM', HAS: 'HAS', Gestante: 'Gestante', Idoso: '60+' };
    const s = styles[type] || {};
    return (
        <span style={{
            ...s, padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem',
            fontWeight: '700', marginRight: '3px', border: '1px solid',
            borderColor: s.borderColor, letterSpacing: '0.03em'
        }} title={missingInfo ? "Dados pendentes" : ""}>
            {labels[type]}
            {missingInfo && <span style={{ marginLeft: '2px', color: '#ef4444' }}>!</span>}
        </span>
    );
};

const S = {
    page: { paddingBottom: '40px', minHeight: '100vh', background: '#f0f9f4', fontFamily: "'Plus Jakarta Sans', sans-serif" },
    header: {
        background: 'linear-gradient(135deg, #065f46 0%, #047857 60%, #059669 100%)',
        padding: '20px 32px', boxShadow: '0 2px 20px rgba(5,150,105,0.3)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: '16px'
    },
    headerLeft: {},
    pageTitle: { margin: 0, fontSize: '1.4rem', fontWeight: '800', color: 'white', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '10px' },
    headerIconBox: { width: '32px', height: '32px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    urlPath: { color: 'rgba(255,255,255,0.7)', margin: '5px 0 0 42px', fontSize: '0.8rem', fontWeight: '500' },
    searchContainer: { padding: '20px 24px 0', maxWidth: '1400px', margin: '0 auto' },
    searchBar: {
        display: 'flex', alignItems: 'center',
        background: 'white', border: '2px solid #e2e8f0', borderRadius: '12px',
        padding: '12px 18px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        marginBottom: '0',
    },
    searchInput: { border: 'none', outline: 'none', width: '100%', marginLeft: '12px', fontSize: '0.95rem', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '500', color: '#1e293b', background: 'transparent' },
    container: { margin: '0 24px', background: 'transparent', boxShadow: 'none', border: 'none' },
    toolbarRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' },
    colHeader: { display: 'grid', padding: '0 16px', marginBottom: '8px', color: '#64748b', fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em' },
    card: (isSelected) => ({
        background: 'white', borderRadius: '12px', marginBottom: '16px',
        border: isSelected ? '2px solid #059669' : '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: isSelected ? '0 4px 16px rgba(5,150,105,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
        transition: 'all 0.2s ease',
        fontFamily: "'Plus Jakarta Sans', sans-serif"
    }),
    cardHeader: (isSelected) => ({
        padding: '11px 16px', background: isSelected ? '#f0fdf4' : 'linear-gradient(to right, #f8fafc, #f1f5f9)',
        borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }),
    homeIcon: (isSelected) => ({
        background: isSelected ? '#059669' : '#e0f2fe', padding: '6px', borderRadius: '7px',
        color: isSelected ? 'white' : '#0369a1', cursor: 'pointer', transition: 'all 0.2s ease',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
    }),
    memberRow: { background: 'white', transition: 'background 0.12s' },
    avatar: (isSelected) => ({
        width: '32px', height: '32px', borderRadius: '50%',
        background: isSelected ? '#059669' : '#f0f9f4',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isSelected ? 'white' : '#64748b', fontSize: '0.72rem', fontWeight: '800',
        flexShrink: 0, cursor: 'pointer', transition: 'all 0.2s ease',
        border: isSelected ? '2px solid #047857' : '1px solid #e2e8f0'
    }),
    btnSendToVisit: {
        padding: '8px 16px', background: 'linear-gradient(135deg, #047857, #059669)',
        color: 'white', border: 'none', borderRadius: '9px', cursor: 'pointer',
        fontWeight: '700', fontSize: '0.82rem', display: 'flex', alignItems: 'center',
        gap: '6px', fontFamily: "'Plus Jakarta Sans', sans-serif",
        boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
        transition: 'all 0.15s'
    },
    btnClear: {
        padding: '8px 14px', background: '#fee2e2', color: '#dc2626',
        border: '1px solid #fecaca', borderRadius: '9px', cursor: 'pointer',
        fontWeight: '700', fontSize: '0.82rem', fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: 'all 0.15s'
    },
    select: { padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '600', fontSize: '0.82rem', cursor: 'pointer', background: 'white' },
    paginationBtn: (disabled) => ({
        padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '8px',
        background: disabled ? '#f1f5f9' : 'white', cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', transition: 'all 0.15s',
        color: disabled ? '#94a3b8' : '#334155'
    }),
    pageInfo: { fontSize: '0.85rem', color: '#64748b', fontWeight: '600' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '24px', alignItems: 'center' },
    neverVisited: { color: '#ef4444', fontSize: '0.72rem', fontWeight: '700', background: '#fee2e2', padding: '2px 7px', borderRadius: '5px' },
    missingDum: { display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', fontSize: '0.68rem', fontWeight: '700', background: '#fee2e2', padding: '3px 8px', borderRadius: '5px' },
};

export default function Pacientes() {
    const navigate = useNavigate();
    const [allPatients, setAllPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedHouseholds, setSelectedHouseholds] = useState(new Set());

    const {
        searchTerm, setSearchTerm, sortConfig, setSortConfig,
        activeAlerts, setActiveAlerts, selectedMicroArea, setSelectedMicroArea,
        filteredList: filteredPatients,
    } = usePatientFilters(allPatients, ['nome', 'cns', 'endereco'], { key: 'default', direction: 'ascending' }, false);

    const availableMicroAreas = useMemo(() => {
        const areas = new Set(allPatients.map(p => p.micro_area).filter(Boolean));
        return Array.from(areas).sort();
    }, [allPatients]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, activeAlerts, selectedMicroArea, sortConfig]);

    useEffect(() => {
        const saved = localStorage.getItem('selectedHouseholds');
        if (saved) { try { setSelectedHouseholds(new Set(JSON.parse(saved))); } catch (e) { console.error(e); } }
    }, []);

    useEffect(() => {
        const fetchAllPatients = async () => {
            setLoading(true);
            try {
                // 🔥 Mudança para a V2
                const response = await apiFetch('/api/v2/pacientes') 
                if (response.status === 401 || response.status === 403) { window.location.href = '/login?session_expired=true'; return; }
                const result = await response.json();
                
                // 🔥 Ajuste para as flags booleanas (is_gestante) e para evitar o "Missing Dum" falso-positivo
                const data = (result.data || []).map(p => ({ 
                    ...p, 
                    idade: (p.idade !== undefined && p.idade !== -1) ? p.idade : calculateAge(p.dt_nascimento), 
                    missingDum: (p.is_gestante === true && !p.dum) 
                }));
                setAllPatients(data);
            } catch (error) { console.error("Erro:", error); } finally { setLoading(false); }
        };
        fetchAllPatients();
    }, []);

    const processedHouseholds = useMemo(() => {
        const groups = {};
        filteredPatients.forEach(p => {
            const key = getAddressKey(p);
            if (!groups[key]) groups[key] = { key, representative: p, members: [] };
            groups[key].members.push(p);
        });
        let households = Object.values(groups);
        households.sort((a, b) => {
            if (sortConfig.key === 'ultima_visita') {
                const getMax = (h) => Math.max(...h.members.map(m => m.ultima_visita ? new Date(m.ultima_visita).getTime() : 0));
                return sortConfig.direction === 'ascending' ? getMax(a) - getMax(b) : getMax(b) - getMax(a);
            }
            const addrA = (a.representative.endereco || '').toLowerCase();
            const addrB = (b.representative.endereco || '').toLowerCase();
            if (addrA !== addrB) return addrA.localeCompare(addrB);
            const numA = parseInt(a.representative.numero) || 0;
            const numB = parseInt(b.representative.numero) || 0;
            if (numA !== numB) return numA - numB;
            return (a.representative.complemento || '').toLowerCase().localeCompare((b.representative.complemento || '').toLowerCase());
        });
        households.forEach(house => {
            house.members.sort((a, b) => {
                if (a.responsavel_familiar === 1 && b.responsavel_familiar !== 1) return -1;
                if (a.responsavel_familiar !== 1 && b.responsavel_familiar === 1) return 1;
                return b.idade - a.idade;
            });
        });
        return households;
    }, [filteredPatients, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentHouseholds = processedHouseholds.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(processedHouseholds.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };

    const toggleHouseholdSelection = (houseKey) => {
        setSelectedHouseholds(prev => {
            const newSet = new Set(prev);
            newSet.has(houseKey) ? newSet.delete(houseKey) : newSet.add(houseKey);
            localStorage.setItem('selectedHouseholds', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const toggleAllOnPage = () => {
        const allKeysOnPage = currentHouseholds.map(h => h.key);
        const allSelected = allKeysOnPage.every(key => selectedHouseholds.has(key));
        setSelectedHouseholds(prev => {
            const newSet = new Set(prev);
            allSelected ? allKeysOnPage.forEach(key => newSet.delete(key)) : allKeysOnPage.forEach(key => newSet.add(key));
            localStorage.setItem('selectedHouseholds', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const sendToVisitPage = () => {
        if (selectedHouseholds.size === 0) { alert('Selecione ao menos um domicílio!'); return; }
        const householdsToSend = processedHouseholds.filter(h => selectedHouseholds.has(h.key));
        const visitData = householdsToSend.map(house => ({ key: house.key, addressDisplay: formatAddressDisplay(house.representative), members: house.members }));
        localStorage.setItem('visitQueueData', JSON.stringify(visitData));
        navigate('/folha-visita');
    };

    const clearSelection = () => { setSelectedHouseholds(new Set()); localStorage.removeItem('selectedHouseholds'); };

    const allOnPageSelected = currentHouseholds.length > 0 && currentHouseholds.every(h => selectedHouseholds.has(h.key));
    const gridLayout = '2.5fr 1.5fr 1.2fr 1fr';

    return (
        <div style={S.page}>
            {/* ─── HEADER ─────────────────────────── */}
            <header style={S.header}>
                <div style={S.headerLeft}>
                    <h1 style={S.pageTitle}>
                        <div style={S.headerIconBox}><ClipboardList size={18} color="white" /></div>
                        Gerenciamento de Domicílios
                    </h1>
                    <p style={S.urlPath}>
                        <strong style={{ color: 'white' }}>{processedHouseholds.length}</strong> domicílios na filtragem atual
                        {selectedHouseholds.size > 0 && (
                            <span style={{ marginLeft: '12px', color: '#6ee7b7', fontWeight: '700' }}>
                                · {selectedHouseholds.size} selecionado(s)
                            </span>
                        )}
                    </p>
                </div>
            </header>

            {/* ─── SEARCH ─────────────────────────── */}
            <div style={{ ...S.searchContainer, paddingBottom: '0' }}>
                <div style={S.searchBar}>
                    <Search size={20} color="#94a3b8" style={{ flexShrink: 0 }} />
                    <input
                        type="text"
                        placeholder="Buscar por nome, CNS, rua ou número..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={S.searchInput}
                    />
                </div>
            </div>

            {/* FILTROS */}
            <FilterControls
                sortConfig={sortConfig} setSortConfig={setSortConfig}
                activeAlerts={activeAlerts} setActiveAlerts={setActiveAlerts}
                microAreas={availableMicroAreas} selectedMicroArea={selectedMicroArea}
                setSelectedMicroArea={setSelectedMicroArea}
                hiddenFilters={[]} hiddenSorts={['endereco']}
            />

            {/* ─── TABELA ─────────────────────────── */}
            <div style={{ ...S.container, margin: '16px 24px' }}>

                {/* TOOLBAR */}
                <div style={S.toolbarRow}>
                    <div>Exibindo <strong>{currentHouseholds.length}</strong> de <strong>{processedHouseholds.length}</strong> residências</div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {selectedHouseholds.size > 0 && (
                            <>
                                <button style={S.btnSendToVisit} onClick={sendToVisitPage}>
                                    Enviar para Folha de Visita ({selectedHouseholds.size})
                                </button>
                                <button style={S.btnClear} onClick={clearSelection}>Limpar Seleção</button>
                            </>
                        )}
                        <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} style={S.select}>
                            <option value={10}>10 por página</option>
                            <option value={20}>20 por página</option>
                            <option value={50}>50 por página</option>
                        </select>
                    </div>
                </div>

                {/* COL HEADER */}
                {processedHouseholds.length > 0 && (
                    <div style={{ ...S.colHeader, gridTemplateColumns: gridLayout }}>
                        <div style={{ paddingLeft: '42px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage}
                                style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: '#059669' }}
                                title={allOnPageSelected ? "Desmarcar todos desta página" : "Marcar todos desta página"} />
                            <Users size={12} /> Paciente
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Activity size={12} /> Condições</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><CalendarClock size={12} /> Últ. Visita</div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}><AlertTriangle size={12} /> Ações</div>
                    </div>
                )}

                {/* CARDS */}
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontWeight: '500' }}>Carregando...</div>
                ) : (
                    currentHouseholds.map((house) => {
                        const isSelected = selectedHouseholds.has(house.key);
                        return (
                            <div key={house.key} style={S.card(isSelected)}>
                                {/* CABEÇALHO DO CARD */}
                                <div style={S.cardHeader(isSelected)}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={S.homeIcon(isSelected)} onClick={() => toggleHouseholdSelection(house.key)}
                                            title={isSelected ? "Clique para desmarcar" : "Clique para selecionar"}>
                                            {isSelected ? <Check size={17} /> : <Home size={17} />}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>
                                                {formatAddressDisplay(house.representative)}
                                            </div>
                                            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '1px', fontWeight: '500' }}>
                                                {house.representative.bairro} · {house.representative.micro_area ? `MA ${house.representative.micro_area}` : 'MA n/d'} · {house.members.length} morador(es)
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* MEMBROS */}
                                <div>
                                    {house.members.map((p, idx) => (
                                        <div key={p.cns} style={{
                                            display: 'grid', gridTemplateColumns: gridLayout,
                                            alignItems: 'center', padding: '11px 16px',
                                            borderBottom: idx === house.members.length - 1 ? 'none' : '1px solid #f1f5f9',
                                            background: 'white', transition: 'background 0.12s',
                                        }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#fafffe'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>

                                            {/* NOME */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={S.avatar(isSelected)} onClick={() => toggleHouseholdSelection(house.key)}
                                                    title={isSelected ? "Desmarcar domicílio" : "Marcar domicílio"}>
                                                    {isSelected ? <Check size={15} /> : p.nome.charAt(0)}
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nome}</div>
                                                    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '500' }}>{p.idade} anos · CNS: {p.cns}</div>
                                                </div>
                                            </div>

                                            {/* CONDIÇÕES */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                                <ConditionBadge type="HAS" active={p.is_hipertenso === true} />
                                                <ConditionBadge type="DM" active={p.is_diabetico === true} />
                                                <ConditionBadge type="Gestante" active={p.is_gestante === true} missingInfo={p.missingDum} />
                                                <ConditionBadge type="Idoso" active={p.idade >= 60} />
                                                {(!p.is_hipertenso && !p.is_diabetico && !p.is_gestante && p.idade < 60) && (
                                                    <span style={{ fontSize: '0.72rem', color: '#cbd5e1', fontWeight: '500' }}>—</span>
                                                )}
                                            </div>

                                            {/* ÚLTIMA VISITA */}
                                            <div style={{ fontSize: '0.8rem', color: '#475569', fontWeight: '500' }}>
                                                {p.ultima_visita ? (
                                                    <span>{formatDate(p.ultima_visita)}</span>
                                                ) : (
                                                    <span style={S.neverVisited}>Nunca visitado</span>
                                                )}
                                            </div>

                                            {/* AÇÕES */}
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                {p.missingDum && (
                                                    <div title="Atualizar cadastro" style={S.missingDum}>
                                                        <AlertTriangle size={11} /> SEM DUM
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* PAGINAÇÃO */}
            {totalPages > 1 && (
                <div style={S.pagination}>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                        style={S.paginationBtn(currentPage === 1)}>
                        <ChevronLeft size={15} />
                    </button>
                    <span style={S.pageInfo}>Página {currentPage} de {totalPages}</span>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                        style={S.paginationBtn(currentPage === totalPages)}>
                        <ChevronRight size={15} />
                    </button>
                </div>
            )}
        </div>
    );
}